// app/frontend/Pages/Events/EditEvent.tsx
import React, { useMemo } from 'react';
import { Head, usePage } from '@inertiajs/react';
import { Container, Paper, Text, Box, Button, Card, Title, Stack, Tooltip, Alert, Group, Badge } from '@mantine/core';
import {
  IconCalendarPlus,
  IconUsers,
  IconClipboardText,
  IconMail,
  IconChartBar,
  IconDots,
  IconCopy,
  IconCheck,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';

import EventForm from '../../components/EventForm/EventForm';

import { LightCappedBadge, TabLayout, DangerZoneCard, AdminCard } from '../../components/ui';

import type { EventFormProps } from '../../components/EventForm/types';
import { useUser } from '../../src/context/UserContext';

// Styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dates/styles.css';
import '../../styles/Login-form.css';
import '../../styles/ScrollTabs.css';

interface User {
  id: number;
  email: string;
  username: string;
  avatar_url: string | null;
  roles: string[];
  organizer_ids?: number[];
}

interface RecurringEvent {
  id: number;
  title: string;
}

interface EditEventPageProps extends EventFormProps {
  event: EventFormProps['event'] & {
    recurring_event?: RecurringEvent;
  };
}

export default function EditEvent() {
  const { props } = usePage<EditEventPageProps>();
  const { user } = useUser() as { user: User | null };

  // Duplication loading states
  const [isDuplicatingEvent, setIsDuplicatingEvent] = useState(false);
  const [isDuplicatingRecurring, setIsDuplicatingRecurring] = useState(false);

  // Status mapping
  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    props.event.event_status_id && statusMapping[props.event.event_status_id]
      ? statusMapping[props.event.event_status_id]
      : { status: 'Unknown', color: 'gray', icon: <IconInfoCircle size={16} /> };

  // Permission checks
  const canDuplicate = useMemo(() => {
    if (!user) return false;
    if (user.roles?.includes('SuperAdmin')) return true;
    if (user.roles?.includes('Editor')) return true;
    if (props.event.user_id === user.id) return true;
    if (props.event.organizer_ids?.includes(user.id)) return true;
    return false;
  }, [user, props.event]);

  // Duplication functions
  const duplicateEvent = async (duplicateAs: 'single' | 'recurring') => {
    if (duplicateAs === 'single') {
      setIsDuplicatingEvent(true);
    } else {
      setIsDuplicatingRecurring(true);
    }

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        throw new Error('CSRF token not found');
      }

      const response = await fetch(`/events/${props.event.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ duplicate_as: duplicateAs }),
      });

      const data = await response.json();

      if (data.success) {
        notifications.show({
          title: 'Success',
          message: 'Event duplicated successfully. Please review and submit.',
          color: 'green',
          icon: <IconCheck />,
          autoClose: 5000,
          position: 'top-right',
        });

        setTimeout(() => {
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          } else {
            window.location.href = `/suggest_event?edit_draft=${data.event_id}`;
          }
        }, 1000);
      } else {
        throw new Error(data.error || 'Failed to duplicate event');
      }
    } catch (error) {
      console.error('Duplication error:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'An error occurred while duplicating the event',
        color: 'red',
        icon: <IconInfoCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsDuplicatingEvent(false);
      setIsDuplicatingRecurring(false);
    }
  };

  // Tab definitions
  const tabs = [
    { id: 'overview', label: 'Overview', icon: <IconCalendarPlus size={16} /> },
    { id: 'attendees', label: 'Attendees', icon: <IconUsers size={16} /> },
    { id: 'registration', label: 'Registration', icon: <IconClipboardText size={16} /> },
    { id: 'blasts', label: 'Blasts', icon: <IconMail size={16} /> },
    { id: 'insights', label: 'Insights', icon: <IconChartBar size={16} /> },
    { id: 'more', label: 'More', icon: <IconDots size={16} /> },
  ];

  // Tab content mapping
  const tabContent = {
    overview: (
      <EventForm
        event={props.event}
        artists={props.artists}
        event_types={props.event_types}
        dance_styles={props.dance_styles}
        event_frequencies={props.event_frequencies}
        organizers={props.organizers}
        errors={props.errors}
        areas={props.areas}
        cities={props.cities}
        users={props.users}
        event_statuses={props.event_statuses}
      />
    ),
    attendees: (
      <Box p="md">
        <Text size="md" c="white" ta="left">
          Attendees management coming soon...
        </Text>
      </Box>
    ),
    registration: (
      <Box p="md">
        <Text size="md" c="white" ta="left">
          Registration settings coming soon...
        </Text>
      </Box>
    ),
    blasts: (
      <Box p="md">
        <Text size="md" c="white" ta="left">
          Email & Text blasts coming soon...
        </Text>
      </Box>
    ),
    insights: (
      <Box p="md">
        <Text size="md" c="white" ta="left">
          Event insights coming soon...
        </Text>
      </Box>
    ),
    more: (
      <Box>
        {/* Event Duplication Section */}
        {canDuplicate && (
          <Card mb="xl" p="lg" style={{ backgroundColor: '#4a4a4a', border: '1px solid #666' }}>
            <Title order={4} c="white" mb="md">
              Duplicate As A Single Event
            </Title>

            <Stack gap="md">
              {/* Duplicate Event Button */}
              <Tooltip label="Create a copy of this event as a single event" position="top" withArrow>
                <Button
                  leftSection={<IconCopy size={16} />}
                  variant="filled"
                  color="orange"
                  size="md"
                  loading={isDuplicatingEvent}
                  onClick={() => duplicateEvent('single')}
                  style={{ marginLeft: 'auto', display: 'block' }}
                >
                  Duplicate Event (Single)
                </Button>
              </Tooltip>

              {/* Recurring Event Options */}
              {props.event.is_recurring && (
                <Box>
                  <Title order={4} c="white" my="md">
                    Recurring Event Options:
                  </Title>

                  <Stack gap="xs">
                    <Tooltip
                      label="Create a copy of this recurring event series (best for short-term series, 4-8 weeks)"
                      position="top"
                      withArrow
                    >
                      <Button
                        leftSection={<IconCopy size={16} />}
                        variant="filled"
                        color="orange"
                        loading={isDuplicatingRecurring}
                        onClick={() => duplicateEvent('recurring')}
                        size="md"
                        disabled
                        style={{ marginLeft: 'auto', display: 'block' }}
                      >
                        Duplicate Recurring Event Series
                      </Button>
                    </Tooltip>

                    <Tooltip
                      label="Add more events to long-standing recurring series (best for 8+ weeks series)"
                      position="top"
                      withArrow
                    >
                      <Button
                        leftSection={<IconCopy size={16} />}
                        variant="filled"
                        color="orange"
                        size="md"
                        disabled
                        style={{ marginLeft: 'auto', display: 'block' }}
                      >
                        Add Events to Long-term Recurring Events
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* Danger Zone */}
        <DangerZoneCard eventId={props.event.id!} eventName={props.event.name} />
      </Box>
    ),
  };

  return (
    <>
      <Head title="Edit Event" />

      <Container size="sm" mb="3rem" style={{ minHeight: '75vh' }}>
        {user?.roles?.includes('SuperAdmin') && <AdminCard entity={props.event} entityType="Event" />}
        <Paper p="lg" shadow="xs" style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }} pos="relative">
          {/* Header with Event Status */}
          <Group justify="space-between" align="center" mb="lg">
            <Title order={3} c="#E4E4E7">
              Edit Event: {props.event.name}
            </Title>
            <LightCappedBadge color={eventStatus.color} icon={eventStatus.icon}>
              {eventStatus.status}
            </LightCappedBadge>
          </Group>

          {/* Recurring Event Alert */}
          {props.event.recurring_event && (
            <Alert
              icon={<IconInfoCircle size="1.5rem" />}
              title="Editing a Recurring Event Instance"
              color="orange"
              radius="sm"
              variant="light"
              mb="md"
            >
              <Text c="#F2F2F2">
                You are currently editing a single instance of the recurring event:{' '}
                <strong>{props.event.recurring_event.title}</strong>.
              </Text>
              <Text mt="sm" c="#f2f2f2">
                These changes will <strong>not</strong> affect the other occurrences of this event.
              </Text>
            </Alert>
          )}

          {/* Tabbed Content */}
          <TabLayout tabs={tabs} content={tabContent} defaultTab="overview" color="orange" />
        </Paper>
      </Container>
    </>
  );
}
